import React from "react";
import { verdictBg, verdictLabel } from "@/lib/format";
import { Select as ShadSelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function VerdictBadge({ verdict, size = "md", testid }) {
    const sz =
        size === "lg"
            ? "text-base px-[18px] py-2"
            : size === "sm"
              ? "text-[10px] px-2.5 py-1"
              : "text-xs px-3.5 py-1.5";
    return (
        <span
            data-testid={testid || `verdict-${verdict}`}
            className={`inline-flex items-center gap-2 border-2 border-black font-mono uppercase tracking-[0.2em] font-bold ${verdictBg(verdict)} text-black ${sz}`}
        >
            <span className="h-1.5 w-1.5 bg-black inline-block" />
            {verdictLabel(verdict)}
        </span>
    );
}

export function ScoreBlock({ score, size = "md" }) {
    const sz =
        size === "lg"
            ? "text-7xl"
            : size === "sm"
              ? "text-2xl"
              : "text-4xl";
    return (
        <div className="inline-flex items-baseline gap-2">
            <span className={`font-display leading-none ${sz}`}>
                {score ?? "—"}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                / 100
            </span>
        </div>
    );
}

export function Eyebrow({ children, className = "", as: Tag = "span" }) {
    return (
        <Tag
            className={`font-mono text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 ${className}`}
        >
            {children}
        </Tag>
    );
}

export function PageHeader({ eyebrow, title, actions, description }) {
    return (
        <div className="border-b-2 border-black bg-white">
            <div className="px-6 md:px-10 py-6 md:py-8 flex flex-wrap items-end justify-between gap-6">
                <div>
                    {eyebrow && <Eyebrow>── {eyebrow}</Eyebrow>}
                    <h1
                        className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-none mt-2"
                        data-testid="page-title"
                    >
                        {title}
                    </h1>
                    {description && (
                        <p className="text-sm text-zinc-600 mt-2 max-w-xl leading-relaxed">
                            {description}
                        </p>
                    )}
                </div>
                {actions && (
                    <div className="flex items-center gap-3 flex-wrap">
                        {actions}
                    </div>
                )}
            </div>
        </div>
    );
}

export function EmptyState({ title, description, action, icon: Icon }) {
    return (
        <div
            data-testid="empty-state"
            className="border-2 border-dashed border-black bg-white p-10 text-center"
        >
            {Icon && <Icon size={32} className="mx-auto mb-4" strokeWidth={1.5} />}
            <div className="font-display text-2xl uppercase tracking-tight">
                {title}
            </div>
            {description && (
                <p className="mt-2 text-sm text-zinc-600 max-w-md mx-auto">
                    {description}
                </p>
            )}
            {action && <div className="mt-6 inline-block">{action}</div>}
        </div>
    );
}

export function Btn({
    children,
    onClick,
    href,
    target,
    variant = "outline",
    size = "md",
    type,
    disabled,
    icon: Icon,
    testid,
    className = "",
    title,
    ref,
}) {
    const base =
        "btn-brutal inline-flex items-center justify-center gap-2 border-2 border-black font-mono uppercase tracking-[0.2em] disabled:opacity-40 disabled:pointer-events-none";
    const sz =
        size === "sm"
            ? "text-[10px] px-3 py-2 shadow-brutal-sm"
            : size === "lg"
              ? "text-sm px-7 py-4 shadow-brutal-sm"
              : "text-[11px] px-5 py-2.5 shadow-brutal-sm";
    const variants = {
        primary: "bg-black text-white",
        outline: "bg-white text-black",
        yellow: "bg-[#FFCC00] text-black",
        danger: "bg-[#FF3B30] text-white",
        ghost: "border-transparent shadow-none hover:bg-[var(--surface-container-high)]",
    };
    const Cmp = href ? "a" : "button";
    return (
        <Cmp
            ref={ref}
            href={href}
            target={target}
            rel={target === "_blank" ? "noopener noreferrer" : undefined}
            type={type || (href ? undefined : "button")}
            onClick={onClick}
            disabled={disabled}
            title={title}
            data-testid={testid}
            className={`${base} ${sz} ${variants[variant] || variants.outline} ${className}`}
        >
            {Icon && <Icon size={size === "sm" ? 12 : 14} />}
            {children}
        </Cmp>
    );
}

export function Field({ label, children, hint, testid }) {
    return (
        <label
            data-testid={testid ? `field-${testid}` : undefined}
            className="block"
        >
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-1.5">
                {label}
            </div>
            {children}
            {hint && (
                <div className="font-mono text-[10px] text-zinc-500 mt-1">
                    {hint}
                </div>
            )}
        </label>
    );
}

export function TextInput({ value, onChange, placeholder, type = "text", testid, onKeyDown, autoFocus, className = "" }) {
    return (
        <input
            data-testid={testid}
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange?.(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className={`w-full border-2 border-black bg-white px-3 py-2 font-mono text-sm focus:outline-none focus:bg-zinc-50 ${className}`}
        />
    );
}

export function Select({ value, onChange, options, testid }) {
    return (
        <ShadSelect value={value ?? ""} onValueChange={(v) => onChange?.(v)}>
            <SelectTrigger data-testid={testid} className="w-full border-2 border-black bg-white dark:bg-zinc-900 px-3 py-2 font-mono text-xs uppercase tracking-[0.15em]">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                        {o.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </ShadSelect>
    );
}

export function Card({ children, className = "", testid, onClick, ...props }) {
    const interactive = typeof onClick === 'function';
    return (
        <div
            data-testid={testid}
            className={`border-2 border-black bg-white dark:bg-zinc-900 ${interactive ? 'cursor-pointer' : ''} ${className}`}
            onClick={onClick}
            {...(interactive ? { tabIndex: 0, role: "button", onKeyDown: (e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick(e);
                }
            } } : {})}
            {...props}
        >
            {children}
        </div>
    );
}

export function SectionTitle({ children, kicker }) {
    return (
        <div className="flex items-baseline gap-3 mb-4">
            {kicker && <Eyebrow>── {kicker}</Eyebrow>}
            <h2 className="font-display text-xl md:text-2xl uppercase tracking-tight leading-none">
                {children}
            </h2>
        </div>
    );
}
